<script>
import Vue from 'vue';
import FileUpload from 'vue-upload-component';
import { Fancybox } from '@fancyapps/ui';
import {
    debounce,
} from 'lodash';
import FilePreview from '@components/FilePreview.vue';
import apiClient from '@services/ApiClient';
import {
    formatDateTime,
} from '@services/Helpers';
import LimitlessButtonDropdown from '@components/Limitless/LimitlessButtonDropdown.vue';

const IMAGE_EXTENSIONS = ['apng', 'avif', 'bmp', 'gif', 'heic', 'heif', 'ico', 'jpeg', 'jpg', 'png', 'svg', 'webp'];
const TEXT_EXTENSIONS = ['csv', 'htm', 'html', 'json', 'log', 'sql', 'text', 'toml', 'tsv', 'txt', 'xml', 'yaml', 'yml'];
const MARKDOWN_EXTENSIONS = ['markdown', 'md', 'mdown'];
const FilePreviewComponent = Vue.extend(FilePreview);

export default {
    name: 'FilListUploader',
    components: {
        FileUpload,
        LimitlessButtonDropdown,
    },
    props: {
        recursive: { type: Boolean, default: false },
        extensions: {
            type: Array,
            default() {
                return [];
            },
        },
        accept: {
            type: String,
            default: '',
        },
        meta: {
            type: Object,
            default() {
                return {};
            },
        },
        multiple: {
            type: Boolean,
            default: false,
        },
        maximum: {
            type: Number,
            default() {
                return this.multiple ? 0 : 1;
            },
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        message: {
            type: String,
            default: '',
        },
        autoStartUpload: {
            type: Boolean,
            default: false,
        },
        displayList: {
            type: Boolean,
            default: false,
        },
        id: {
            type: String,
            default: 'image-grid',
        },
        modelType: {
            type: String,
            default: null,
        },
        modelId: {
            type: [Number, String],
            default: null,
        },
        noOuterBorder: {
            type: Boolean,
            default: false,
        },
        header: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            busy_loading_files: true,
            files: [],
            showHistory: false,
            upload_files: [],
            headers: {
                'X-CSRF-TOKEN': 'xxxx',
            },
            edit_media: null,
            fanzy: null,
            fancyPreviewApps: {},
        };
    },
    watch: {
        upload_files: {
            deep: true,
            handler() {
                this.updateBusy();
            },
        },
    },
    created() {
        this.getFiles();
        const token = document.head.querySelector('meta[name="csrf-token"]');
        if (token) {
            this.headers['X-CSRF-TOKEN'] = token.content;
        }
    },
    beforeDestroy() {
        this.destroyFancybox();
    },
    methods: {
        formatDateTime,
        getFiles() {
            this.busy_loading_files = true;
            apiClient.get(this.real_list_url)
                .then((response) => {
                    this.files = response.data;
                })
                .finally(() => {
                    this.busy_loading_files = false;
                });
        },
        getFileExtension(file) {
            return (file?.fileext || '').toLowerCase().replace(/^\./, '');
        },
        getFileMimeType(file) {
            return (file?.mime_type || file?.mime || '').toLowerCase();
        },
        isImagePreview(file) {
            return this.getFileMimeType(file).startsWith('image/')
                || IMAGE_EXTENSIONS.includes(this.getFileExtension(file));
        },
        isPdfPreview(file) {
            return this.getFileMimeType(file) === 'application/pdf'
                || this.getFileExtension(file) === 'pdf';
        },
        isMarkdownPreview(file) {
            return MARKDOWN_EXTENSIONS.includes(this.getFileExtension(file))
                || ['text/markdown', 'text/x-markdown'].includes(this.getFileMimeType(file));
        },
        isTextPreview(file) {
            return this.isMarkdownPreview(file)
                || this.getFileMimeType(file).startsWith('text/')
                || ['application/json', 'application/xml', 'application/x-yaml'].includes(this.getFileMimeType(file))
                || TEXT_EXTENSIONS.includes(this.getFileExtension(file));
        },
        canPreviewInFancybox(file) {
            return this.isImagePreview(file) || this.isPdfPreview(file) || this.isTextPreview(file);
        },
        buildFancyboxCaption(file) {
            return [
                file.filename,
            ].filter((str) => !!str).join(' - ');
        },
        buildFancyboxSlides() {
            return this.files
                .map((file, sourceIndex) => {
                    if (!this.canPreviewInFancybox(file)) {
                        return null;
                    }

                    if (this.isImagePreview(file)) {
                        return {
                            src: file.url_show,
                            thumb: file.url_preview,
                            caption: this.buildFancyboxCaption(file),
                            sourceIndex,
                        };
                    }

                    const previewId = `fglu-preview-${this.id}-${file.filename}-${sourceIndex}`;

                    return {
                        html: `<div class="fglu-fancybox-preview-host h-100" data-preview-id="${previewId}"></div>`,
                        caption: this.buildFancyboxCaption(file),
                        thumb: file.url_preview,
                        sourceIndex,
                        previewId,
                        width: '92%',
                        height: '92%',
                    };
                })
                .filter(Boolean);
        },
        mountFancyboxPreview(slide) {
            if (!slide?.previewId || !slide?.el) {
                return;
            }

            const container = slide.el.querySelector(`[data-preview-id="${slide.previewId}"]`);
            const file = this.files[slide.sourceIndex];

            if (!container || !file) {
                return;
            }

            const existingPreview = this.fancyPreviewApps[slide.previewId];
            if (existingPreview?.container === container && container.childElementCount > 0) {
                return;
            }

            if (existingPreview?.instance) {
                existingPreview.instance.$destroy();
            }

            const mountPoint = document.createElement('div');
            container.innerHTML = '';
            container.appendChild(mountPoint);

            const instance = new FilePreviewComponent({
                propsData: {
                    file,
                    maxHeight: 'calc(85vh - 70px)',
                    showCard: false,
                },
            });

            instance.$mount(mountPoint);
            this.fancyPreviewApps[slide.previewId] = {
                instance,
                container,
            };
        },
        mountCurrentFancyboxPreview(instance) {
            const slide = instance?.getSlide?.();
            this.mountFancyboxPreview(slide);
        },
        unmountFancyboxPreviews() {
            Object.values(this.fancyPreviewApps).forEach((preview) => {
                preview.instance.$destroy();
                if (preview.instance.$el?.parentNode) {
                    preview.instance.$el.parentNode.removeChild(preview.instance.$el);
                }
            });
            this.fancyPreviewApps = {};
        },
        destroyFancybox() {
            this.unmountFancyboxPreviews();

            if (this.fanzy) {
                this.fanzy.destroy();
                this.fanzy = null;
            }
        },
        // eslint-disable-next-line consistent-return
        async inputFilter(newFile, oldFile, prevent) {
            if (newFile && !oldFile) {
                // Before adding a file

                if (this.extensions) {
                    const re = new RegExp('\\.(' + this.extensions.join('|') + ')$', 'i');
                    if (!re.test(newFile.name)) {
                        await this.$swal({
                            text: 'Wrong file type',
                            icon: 'warning',
                            timer: 3000,
                        });
                        return prevent();
                    }
                }

                // Filter system files or hide files
                if (/(\/|^)(Thumbs\.db|desktop\.ini|\..+)$/.test(newFile.name)) {
                    return prevent();
                }

                // Filter php html js file
                if (/\.(php5?|html?|jsx?)$/i.test(newFile.name)) {
                    return prevent();
                }
            }

            if (newFile && newFile.error === '' && newFile.file && (!oldFile || newFile.file !== oldFile.file)) {
                // Create derived file metadata without mutating the callback parameter.
                let blob = '';
                const URL = (window.URL || window.webkitURL);
                if (URL) {
                    blob = URL.createObjectURL(newFile.file);
                }

                let thumb = '';
                if (blob && newFile.type.substr(0, 6) === 'image/') {
                    thumb = blob;
                }

                this.$refs['upload-' + this.id].update(newFile, { blob, thumb });
            }

            // image size
            if (newFile && newFile.error === '' && newFile.type.substr(0, 6) === 'image/' && newFile.blob && (!oldFile || newFile.blob !== oldFile.blob)) {
                // newFile.error = 'image parsing';
                const img = new Image();
                img.onload = () => {
                    this.$refs['upload-' + this.id].update(newFile, { error: '', height: img.height, width: img.width });
                    this.$refs['upload-' + this.id].update(newFile, { data: { ...newFile.data, height: img.height, width: img.width } });
                };
                img.onerror = () => {
                    this.$refs['upload-' + this.id].update(newFile, { error: 'parsing image size' });
                };
                img.src = newFile.blob;
            }
        },

        // add, update, remove File Event
        inputFile(newFile, oldFile) {
            if (newFile && oldFile) {
                // update

                if (newFile.active && !oldFile.active) {
                    // beforeSend

                    // min size
                    if (newFile.size >= 0 && this.minSize > 0 && newFile.size < this.minSize) {
                        this.$refs['upload-' + this.id].update(newFile, { error: 'size' });
                    }
                }

                if (newFile.progress !== oldFile.progress) {
                    // progress
                }

                if (newFile.error && !oldFile.error) {
                    // error
                }

                if (newFile.success && !oldFile.success) {
                    // success
                    if (newFile.response) {
                        this.$emit('file-uploaded', newFile.response);
                        this.files.push(newFile.response.file);
                    }
                }
            }

            if (!newFile && oldFile) {
                // remove
                if (oldFile.success && oldFile.response.id) {
                    // $.ajax({
                    //   type: 'DELETE',
                    //   url: '/upload/delete?id=' + oldFile.response.id,
                    // })
                }
            }

            // Automatically activate upload
            if (Boolean(newFile) !== Boolean(oldFile) || oldFile.error !== newFile.error) {
                if (this.autoStartUpload && !this.$refs['upload-' + this.id].active) {
                    this.$refs['upload-' + this.id].active = true;
                }
            }
        },
        startUpload() {
            this.$emit('busy', true);
            this.$refs['upload-' + this.id].active = true;
        },
        stopUpload() {
            this.$refs['upload-' + this.id].active = false;
            this.$emit('busy', false);
        },
        updateBusy: debounce(function debouncedUpdateBusy() {
            const vm = this;
            let busy = false;
            vm.upload_files.forEach((file) => {
                if (file.active) {
                    busy = true;
                }
            });
            this.$emit('busy', busy);
        }, 500),
        openFilePreview(index) {
            const file = this.files[index];
            if (!this.canPreviewInFancybox(file)) {
                return;
            }

            this.openFancybox(index);
        },
        openFancybox(index) {
            this.destroyFancybox();

            const fancyFiles = this.buildFancyboxSlides();

            const startIndex = fancyFiles.findIndex((file) => file.sourceIndex === index);
            if (startIndex === -1) {
                return;
            }

            this.fanzy = Fancybox.show(
                fancyFiles,
                {
                    trapFocus: false,
                    hideScrollbar: false,
                    startIndex,
                    on: {
                        reveal: (instance, slide) => {
                            this.mountFancyboxPreview(slide);
                        },
                        'Carousel.ready': (instance) => {
                            this.mountCurrentFancyboxPreview(instance);
                        },
                        'Carousel.change': (instance) => {
                            this.mountCurrentFancyboxPreview(instance);
                        },
                        destroy: () => {
                            this.unmountFancyboxPreviews();
                            this.fanzy = null;
                        },
                    },
                    Toolbar: {
                        items: {
                            editDescription: {
                                tpl: '<button class="f-button" title="Edit description"><i class="icon-pencil3" style="font-size: 20px;"></i></button>',
                                click: (e) => {
                                    const slide = e?.instance?.getSlide?.();
                                    if (slide && Number.isInteger(slide.sourceIndex) && this.files[slide.sourceIndex]) {
                                        this.showModalEditMedia(this.files[slide.sourceIndex]);
                                    }
                                },
                            },
                        },
                        display: {
                            left: [
                                'infobar',
                            ],
                            middle: [],
                            right: [
                                'editDescription',
                                'iterateZoom',
                                'slideshow',
                                'fullscreen',
                                'thumbs',
                                'close',
                            ],
                        },
                    },
                },
            );
        },
        async deleteFile(index) {
            if (this.files[index].protected) return;
            let doDelete = false;
            await this.$swal({
                text: 'Är du säker att du vill radera den här filen?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Ja, radera!',
                cancelButtonText: 'Avbryt',
            }).then((result) => {
                if (result.isConfirmed) {
                    doDelete = true;
                }
            });
            if (doDelete) {
                await apiClient.delete(this.real_delete_url + '/' + encodeURIComponent(this.files[index].filename), { params: { path: this.files[index].relative_path || this.files[index].filename } }).then(() => {
                    const newValue = [...this.files];
                    newValue.splice(index, 1);
                    this.files = newValue;
                }).finally(() => {
                    this.$emit('delete-file', index);
                });
            }
        },
    },
    computed: {
        not_uploaded_files() {
            return this.upload_files.filter((file) => !file.success);
        },
        real_upload_url() {
            return `/vue/${this.modelType}/${this.modelId}/files/upload`;
        },
        real_list_url() {
            return `/vue/${this.modelType}/${this.modelId}/files/list${this.recursive ? '?recursive=1' : ''}`;
        },
        real_delete_url() {
            return `/vue/${this.modelType}/${this.modelId}/files/delete`;
        },
        real_meta() {
            const meta = { ...this.meta };
            return meta;
        },
    },
};
</script>

<template>
    <div class="fglu" :id="id">
        <div v-show="$refs['upload-' + this.id] && $refs['upload-' + this.id].dropActive" class="drop-active">
            <h3>Välj eller dra och släpp filer här</h3>
        </div>

        <div class="card mb-0 " :class="{ 'border-0': noOuterBorder, 'shadow-none': noOuterBorder, 'image-grid': !displayList }">
            <div v-if="header" class="card-header bg-light header-elements-inline">
                <h6 class="card-title">{{ header }}</h6>
            </div>
            <div v-if="recursive && files.some((file) => file.history)" class="px-3 py-2">
                <button type="button" class="btn btn-light btn-sm" @click="showHistory = !showHistory">{{ showHistory ? 'Dölj tidigare versioner' : 'Visa tidigare versioner' }}</button>
            </div>
            <div class="table-responsive mb-0" v-if="displayList">
                <table
                    class="table table-xs"
                    :class="{ 'border-bottom': !disabled }"
                >
                    <thead>
                        <tr>
                            <th>Thumb</th>
                            <th>Fil</th>
                            <th>Uppladdad</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        <template v-if="files && files.length > 0">
                            <tr v-for="(file, i_index) in files" v-show="!file.history || showHistory" :key="file.id || file.relative_path || file.filename">
                                <td style="width: 38px;">
                                    <div
                                        :id="'file-thumb-' + i_index"
                                        style="width: 38px; height: 38px; background-repeat: no-repeat; background-position: center; background-size: cover; cursor: pointer;"
                                        :style="{ 'background-image': `url('${file.url_preview}')` }"
                                        @click.self="openFilePreview(i_index)"
                                    ></div>
                                </td>
                                <td>
                                    <span @click.self="openFilePreview(i_index)" style="cursor: pointer;">{{ file.filename }}</span>
                                    <small v-if="recursive && file.relative_path.includes('/')" class="d-block text-muted">{{ file.relative_path }}</small>
                                    <span v-if="file.final" class="badge badge-secondary">Arkivskyddad</span>
                                    <span v-if="file.approved" class="badge badge-success">Godkänt korr</span>
                                </td>
                                <td>
                                    {{ formatDateTime(file.created_ts) }}
                                </td>
                                <td style="width: 50px;">
                                    <div class="d-flex flex-row" style="gap: 2px; white-space: nowrap;">
                                        <limitless-button-dropdown icon-class="icon-menu rotate-90" button-class="btn btn-light dropdown-toggle btn-sm">
                                            <a :href="file.url_show" target="_blank" class="dropdown-item">
                                                <i class="icon-eye"></i> Visa
                                            </a>
                                            <a :href="file.url_download" :download="file.filename" class="dropdown-item">
                                                <i class="icon-file-download"></i> Ladda ner
                                            </a>
                                            <div v-if="!disabled && !file.protected" class="dropdown-divider"></div>
                                            <button v-if="!disabled && !file.protected" type="button" class="dropdown-item" @click="deleteFile(i_index)">
                                                <i class="icon-trash"></i> Radera
                                            </button>
                                        </limitless-button-dropdown>
                                    </div>
                                </td>
                            </tr>
                        </template>
                        <template v-if="not_uploaded_files">
                            <tr v-for="(file, i_index) in not_uploaded_files" :key="'nup-' + i_index">
                                <td>
                                    <div
                                        style="width: 38px; height: 38px; background-color: #cccccc;"
                                    ></div>
                                </td>
                                <td>
                                    <span v-if="file.active" style="z-index: 10">{{ Math.round(file.progress) }} %</span>
                                    <span v-else-if="file.error" style="z-index: 10">Error: {{ file.error }}</span>
                                    <span v-else style="z-index: 10">Köad...</span>
                                </td>
                                <td></td>
                                <td></td>
                            </tr>
                        </template>
                    </tbody>
                </table>
            </div>
            <template v-else>
                <template v-if="files && files.length > 0">
                    <div v-for="(img, i_index) in files" class="image-grid-item" :key="'img-' + i_index">
                        <div class="image-preview" :style="{ 'background-image': `url('${img.url_preview}')`, cursor: canPreviewInFancybox(img) ? 'pointer' : 'default' }" style="position: relative;" @click.self="openFilePreview(i_index)">
                            <div style="position: absolute; top: 0; left: 0; padding: 0 3px; border-radius: 0 0 3px 3px; background: rgba(0, 0, 0, 0.75); color: #ffffff;">
                                #{{ img.id }}
                            </div>
                            <button v-if="!disabled && !img.protected" type="button" class="btn btn-danger" style="position: absolute; top: 10px; right: 10px; padding: 3px 5px;" @click="deleteFile(i_index)">
                                <i class="icon-trash"></i>
                            </button>
                            <a :href="file.url_download" :download="img.filename" class="btn btn-primary" style="position: absolute; bottom: 10px; left: 10px; padding: 3px 5px;">
                                <i class="icon-file-download"></i>
                            </a>
                        </div>
                    </div>
                </template>
                <template v-if="not_uploaded_files">
                    <div v-for="(file, f_index) in not_uploaded_files" class="image-grid-item" :key="'nup-' + f_index">
                        <div class="image-preview opacity-75" style="position: relative; cursor: pointer;" :style="{ 'background-image': `url('${file.thumb}')` }">
                            <span :style="{ width: (file.error ? '100%' : (100 - Math.round(file.progress)) + '%') }" class="position-absolute top-0 right-0 bottom-0 opacity-75" :class="{ 'bg-danger': file.error, 'bg-white': !file.error }"></span>
                        </div>
                        <div class="position-absolute top-0 left-0 bottom-0 right-0 d-flex justify-content-center align-items-center">
                            <span v-if="file.active" style="z-index: 10">{{ Math.round(file.progress) }} %</span>
                            <span v-else-if="file.error" style="z-index: 10">Error: {{ file.error }}</span>
                            <span v-else style="z-index: 10">Köad...</span>
                        </div>
                    </div>
                </template>
            </template>
            <div v-if="!disabled" :class="{ 'image-grid-item': !displayList, 'image-list-item': !displayList }">
                <file-upload
                    class="placeholder button"
                    :url="real_upload_url"
                    :extensions="extensions.join(',')"
                    :accept="accept"
                    :size="1024 * 1024 * 100"
                    :chunk-enabled="true"
                    :chunk="{
                        action: real_upload_url,
                        minSize: 0,
                        maxActive: 1,
                        maxRetries: 1,
                        startBody: real_meta,
                        headers: headers,
                    }"
                    :headers="headers"
                    :data="real_meta"
                    :maximum="maximum"
                    :multiple="multiple"
                    :drop="'#' + id"
                    :drop-directory="true"
                    v-model="upload_files"
                    :ref="'upload-' + id"
                    :input-id="'input-' + id"
                    @input-filter="inputFilter"
                    @input-file="inputFile"
                >
                    <div class="inner">
                        <div><i class="icon-plus3"></i></div>
                        <div v-if="displayList" style="text-overflow: ellipsis;">Välj eller dra och släpp filer här</div>
                    </div>
                </file-upload>
            </div>
        </div>
    </div>
</template>

<style>
.fancybox__slide.has-html .f-html {
    width: min(1100px, 92vw);
    height: min(90vh, 900px);
    padding: 0;
    background: transparent;
}

.fglu-fancybox-preview-host {
    width: 100%;
    height: 100%;
}

.fglu-fancybox-preview-host .file-preview {
    height: 100%;
}

.fglu-fancybox-preview-host .file-preview__text,
.fglu-fancybox-preview-host .file-preview__pdf {
    height: 100%;
}
</style>
